/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Collapse, Button, Badge } from 'reactstrap';
import Alert from 'react-s-alert';
import Joyride, { STATUS } from 'react-joyride';
import moment from 'moment';
import Select from 'react-select'

// context imports
import { ThemeContext } from '../../../contexts/ThemeContext';
import { AuthContext } from '../../../contexts/AuthContext';
// import {SelectedProviderContext} from '../../../contexts/SelectedProviderContext';

// service imports
import DemoService from '../../../services/DemoService';
import ClientService from '../../../services/ClientService';
import GeneralService from '../../../services/GeneralService';

// local imports
import ErrorBoundary from '../../general/ErrorBoundary';
import Message from '../../general/Message';
import SelectedProvider from './SelectedProvider';
import UpcomingSession from '../../user/UpcomingSession';
import CompletedSession from '../../user/CompletedSession';
import CancelledSession from '../../user/CancelledSession';
import RescheduleRequestedSession from '../../user/RescheduleRequestedSession';
import ReservedSession from '../../user/ReservedSession';
import ClientNavbar from './ClientNavbar';
import { counsellors, sessions } from '../demoData';

const ClientDashboard = props => {

    // contexts
    const { selectedTimeZone } = useContext(AuthContext);
    const { theme, colors, fontFamily, fontSize, fontWeight } = useContext(ThemeContext);
    // const { isProviderSelected, selectedProvider } = useContext(SelectedProviderContext);

    // states
    const [user, setUser] = useState({ firstName: "", lastName: "", email: "", selectedRole: '', roles: [], profileImage: "", demoTakenFor: [] });
    const [isRefreshLoading, setIsRefreshLoading] = useState(false);
    const [isOpenMyProvider, setIsOpenMyProvider] = useState(true);
    const [selectedProvider, setSelectedProvider] = useState({ isTrue: false });
    const [profileComplete, setProfileComplete] = useState({
        status: true,
        message: {
            msgBody: ['Looks like your profile is incomplete! Please complete that by going to ', <b key="link"><Link to='/client/profile'>Profile</Link></b>, ' page to enjoy our service.'],
            msgError: true
        }
    });
    const [listOfSessions, setListOfSessions] = useState({
        completedSessions: [],
        upcomingSessions: [],
        cancelledSessions: [],
        rescheduleRequestedSession: [],
        reservedSessions: []
    });
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h5>Welcome to the guided tour of Umang Wellness. We are excited to have you here as a Umang Wellness client to start your journey to your healing.<br />Press Next to continue.</h5>,
                placement: 'center',
                target: '#clientDashboardDiv',
            },
            {
                content: <div className="tooltipDiv"><h5>This is your dashboard!</h5><br />From here you can book and manage your sessions.</div>,
                placement: 'center',
                target: '#clientDashboardDiv',
            },
            {
                content: <div className="tooltipDiv">Start chat with your selected counsellor by pressing this button.</div>,
                placement: 'left',
                target: '#chatBtn',
            },
            {
                content: <div className="tooltipDiv">Refresh your dashboard data by pressing this button.</div>,
                placement: 'left',
                target: '#refreshBtn',
            },
            {
                content: <div className="tooltipDiv">Book a new session by pressing this button.</div>,
                placement: 'bottom',
                target: '#bookBtn',
            },
            {
                content: <div className="tooltipDiv">Select a counsellor of your choice by pressing this button.</div>,
                placement: 'right',
                target: '#findProviderBtn',
            },
            {
                content: <div className="tooltipDiv">Expand and Collapse counsellor details by pressing this button.</div>,
                placement: 'left',
                target: '#myProviderToggle',
            },
            {
                content: <div className="tooltipDiv">These are the details of your preferred counsellor.</div>,
                placement: 'bottom',
                target: '#providerDetails',
            },
            {
                content: <div className="tooltipDiv">Your upcoming sessions are listed here.</div>,
                placement: 'bottom',
                target: '#upcomingSessions',
            },
            {
                content: <div className="tooltipDiv">Get details of your session by pressing this button.</div>,
                placement: 'bottom',
                target: '#toggleSessionUpcoming0'
            },
            {
                content: <div className="tooltipDiv">Rescheduling requests from your counsellor will appear here.</div>,
                placement: 'top',
                target: '#rescheduledSessions',
            },
            {
                content: <div className="tooltipDiv">Completed sessions will appear here.</div>,
                placement: 'top',
                target: '#completedSessions',
            },
            {
                content: <div className="tooltipDiv">Cancelled sessions will appear here.</div>,
                placement: 'top',
                target: '#cancelledSessions',
            },
            {
                content: <div className="tooltipDiv">Navigate to other options by pressing your profile picture.</div>,
                placement: 'left',
                target: '#profileDropdown'
            },
            {
                content: <div className="tooltipDiv"><h5>Hope you enjoyed the quick tour. Please give us your <a style={{ fontWeight: "600", color: "#007bff" }} target="_blank" href="/feedback">feedback</a>.</h5></div>,
                placement: 'center',
                target: '#clientDashboardDiv'
            }
        ]
    });
    let helpers = null;
    const [notifications, setNotifications] = useState({
        newUpcomingSession: [],
        newRescheduleRequest: [],
        newCancelledSession: [],
        newRefundProcessed: [],
        newChatMessage: []
    });

    // useEffect
    useEffect(() => {
        DemoService.getUserData().then(data => {
            if (data.isAuthenticated) {
                if (data.user.roles[0] === "DemoClient") {
                    setUser(data.user);
                    if (!data.user.demoTakenFor.includes("ClientDashboard")) {
                        setTutorial({ ...tutorial, status: true });
                    }
                    setSelectedProvider({ ...counsellors[0], isTrue: true, name: `${counsellors[0].title ? counsellors[0].title : ''} ${counsellors[0].firstName} ${counsellors[0].lastName}` });
                    setListOfSessions({
                        upcomingSessions: sessions,
                        completedSessions: [],//sessions,
                        cancelledSessions: [],//sessions,
                        reservedSessions: [],//sessions,
                        rescheduleRequestedSession: [],//sessions
                    });
                    // setLoading(false);
                } else {
                    props.history.replace('/demo/sessions');
                }
            } else {
                props.history.replace('/demo/signin');
            }
        })
    }, []);

    // utility functions
    const toggleMyProvider = () => {
        try {
            setIsOpenMyProvider(!isOpenMyProvider)
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client ClientDashboard toggleMyCounseler', 'Error in try catch block', err.stack);
        }
    };

    // handle functions
    const handleRefresh = () => {
        setIsRefreshLoading(true);
        setTimeout(() => setIsRefreshLoading(false), [1500])
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (!user.demoTakenFor.includes("ClientDashboard")) {
                DemoService.markVisited("ClientDashboard");
                setUser({ ...user, demoTakenFor: [...user.demoTakenFor, "ClientDashboard"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    // function's return
    return (
        <div id="clientDashboardDiv">
            <ErrorBoundary errorAt="At Client ClientDashboard Joyride">
                <Joyride
                    callback={handleJoyrideCallback}
                    continuous={true}
                    getHelpers={getHelpers}
                    run={tutorial.status}
                    scrollToFirstStep={true}
                    showProgress={false}
                    showSkipButton={true}
                    steps={tutorial.steps}
                    locale={{ last: 'Finish' }}
                    styles={{
                        options: {
                            primaryColor: "rgb(56, 78, 115)",
                            zIndex: 10000,
                        },
                    }}
                />
            </ErrorBoundary>
            <ErrorBoundary errorAt="At Client ClientDashboard ClientNavbar">
                <ClientNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
            </ErrorBoundary>
            <div className="row">
                {/* <h3 className="ml-4" style={{fontFamily: 'Montserrat',fontStyle: 'normal', fontWeight: 'normal',fontSize: '24px'}}>Welcome {user.username}</h3> */}
                <Button id="chatBtn" className="btn ml-auto mr-2" style={theme.button} onClick={() => props.history.push('chat')}>Chat{notifications.newChatMessage.length > 0 ? <Badge className="ml-1" style={{ backgroundColor: colors.warning }}>{notifications.newChatMessage.length}</Badge> : null}</Button>
                <Button id="refreshBtn" className="btn mr-2" style={theme.button} onClick={handleRefresh} disabled={isRefreshLoading}>{isRefreshLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Refresh</Button>
                <Button id="bookBtn" className="btn mr-4" style={theme.button} onClick={() => props.history.push('booksession')}>Book Session</Button>
            </div>
            <div className="px-3 pb-3">

                {profileComplete.status ? null : <Message message={profileComplete.message} />}

                {/* <button className="btn" style={theme.button} onClick={handleBookSession}>Book Session</button> */}

                {/* Selected Provider */}
                <div className="p-3 mt-3" style={theme.card} id="myProvider">
                    <div id="myCounsellor" className="row px-3" onClick={toggleMyProvider} style={{ cursor: "pointer" }}>
                        <span style={theme.headingBold}>My Counsellor </span>

                        {
                            isOpenMyProvider
                                ? <span className="ml-auto" id="myProviderToggle"><i className="fas fa-angle-up"></i></span>
                                : <span className="ml-auto" id="myProviderToggle"><i className="fas fa-angle-down"></i></span>
                        }

                    </div>

                    <Collapse isOpen={isOpenMyProvider}>
                        <hr />

                        <ErrorBoundary errorAt="At Client ClientDashboard SelectedProvider">
                            <SelectedProvider
                                history={props.history}
                                selectedProvider={selectedProvider}
                                selectedTimeZone={selectedTimeZone}
                            />
                        </ErrorBoundary>

                    </Collapse>
                </div>

                <ErrorBoundary errorAt="At Client ClientDashboard ReservedSession">
                    {
                        listOfSessions.reservedSessions.length > 0 ?
                            <ReservedSession sessions={listOfSessions.reservedSessions} history={props.history} /*newUpcomingSession={notifications.newUpcomingSession}*/ />
                            : null
                    }
                </ErrorBoundary>

                <ErrorBoundary errorAt="At Client ClientDashboard UpcomingSession">
                    <UpcomingSession selectedTimeZone={selectedTimeZone} sessions={listOfSessions.upcomingSessions} history={props.history} newUpcomingSession={notifications.newUpcomingSession} />
                </ErrorBoundary>

                <ErrorBoundary errorAt="At Client ClientDashboard RescheduleRequestedSession">
                    <RescheduleRequestedSession selectedTimeZone={selectedTimeZone} sessions={listOfSessions.rescheduleRequestedSession} history={props.history} newRescheduleRequest={notifications.newRescheduleRequest} />
                </ErrorBoundary>

                <ErrorBoundary errorAt="At Client ClientDashboard CompletedSession">
                    <CompletedSession selectedTimeZone={selectedTimeZone} sessions={listOfSessions.completedSessions} />
                </ErrorBoundary>

                <ErrorBoundary errorAt="At Client ClientDashboard CancelledSession">
                    <CancelledSession selectedTimeZone={selectedTimeZone} sessions={listOfSessions.cancelledSessions} newCancelledSession={notifications.newCancelledSession} newRefundProcessed={notifications.newRefundProcessed} />
                </ErrorBoundary>

            </div>

        </div>

    )

}

export default ClientDashboard;